export const formatCurrency = (
	number: number,
	showDecimal: boolean = true,
	showSymbol: boolean = true,
): string => {
	if (number < 0) {
		number = 0; // Reset negative numbers to 0
	}
	if (number === 0) return showSymbol ? "₹0" : "0"; // Handle zero case (now also handles reset negatives)
	if (Math.abs(number) < 1)
		return `${showSymbol ? "₹" : ""}${number.toFixed(showDecimal ? 1 : 0)}`; // Preserve small values

	const isNegative = number < 0; // This will now always be false if the initial number was < 0

	const formats = [
		{ threshold: 1e7, divisor: 1e7, suffix: " Cr" }, // Crore
		{ threshold: 1e5, divisor: 1e5, suffix: " L" }, // Lakh
		{ threshold: 1e3, divisor: 1e3, suffix: " K" }, // Thousand
	];

	for (const { threshold, divisor, suffix } of formats) {
		if (number >= threshold) {
			let formattedValue;
			if (suffix === "L" || suffix === "Cr") {
				// Calculate with 2 decimal places, then use parseFloat().toString()
				// to remove unnecessary trailing zeros (e.g., 1.00 -> "1", 1.50 -> "1.5", 1.56 -> "1.56").
				formattedValue = parseFloat((number / divisor).toFixed(2)).toString();
			} else {
				// For "K" or other formats — strip trailing zeros (e.g. 566.00 → 566, 28.30 → 28.3)
				formattedValue = parseFloat(
					(number / divisor).toFixed(showDecimal ? 2 : 0),
				).toString();
			}
			return `${isNegative ? "-" : ""}${showSymbol ? "₹" : ""}${formattedValue}${suffix}`;
		}
	}

	return `${isNegative ? "-" : ""}${showSymbol ? "₹" : ""}${parseFloat(number.toFixed(showDecimal ? 2 : 0)).toString()}`;
};

export const formatCommaSeparatedCurrency = (
	number: number,
	showSymbol: boolean = true,
): string => {
	const rounded = Math.round(Number(number));
	const formatted = rounded.toLocaleString("en-IN");
	return showSymbol ? `₹${formatted}` : formatted;
};

export const getBarValue = (leaseValue: number, loanValue: number): number => {
	if (loanValue === 0) {
		return 30; // Default value for division by zero
	}
	const percentage = (leaseValue / loanValue) * 100;
	// Check for NaN, which can also occur if leaseValue or loanValue are problematic
	if (isNaN(percentage)) {
		return 30; // Default value if the calculation results in NaN
	}
	return percentage;
};

export const unformatIndianCurrency = (value: string): string => {
	return value.replace(/,/g, "");
};

// Helper function to format number to Indian currency string
export const formatToIndianCurrency = (value: string): string => {
	const cleanValue = unformatIndianCurrency(value);

	if (cleanValue === "" || cleanValue === undefined || cleanValue === null)
		return "";

	// Allow user to type a leading decimal point or just a decimal point
	if (cleanValue === ".") return ".";

	const parts = cleanValue.split(".");
	const integerPartString = parts[0];
	const decimalPartString = parts.length > 1 ? parts[1] : undefined;

	let formattedIntegerPart: string;

	if (integerPartString === "") {
		if (decimalPartString !== undefined) {
			// Handles input like ".5"
			formattedIntegerPart = "0";
		} else {
			return cleanValue; // Fallback for unexpected empty integer part
		}
	} else {
		if (!/^\d+$/.test(integerPartString)) {
			return cleanValue; // Return unformatted if non-digits are present
		}
		try {
			const numToFormat = BigInt(integerPartString);
			formattedIntegerPart = new Intl.NumberFormat("en-IN").format(numToFormat);
		} catch {
			return cleanValue; // Fallback if BigInt or NumberFormat fails
		}
	}

	if (decimalPartString !== undefined) {
		return formattedIntegerPart + "." + decimalPartString;
	} else if (
		cleanValue.endsWith(".") &&
		parts.length === 1 &&
		integerPartString !== ""
	) {
		return formattedIntegerPart + ".";
	} else {
		return formattedIntegerPart;
	}
};

export const isValidSalary = (
	value: string,
): { valid: boolean; message: string } => {
	const numericValue = unformatIndianCurrency(value); // Unformat before validation
	// Rule for empty string
	if (numericValue.trim() === "") {
		return {
			valid: false,
			message: "Please declare your annual CTC.",
		};
	}
	const num = parseFloat(numericValue);
	// Rule for non-numeric or non-positive
	if (isNaN(num) || num <= 0) {
		return {
			valid: false,
			message: "Please enter a valid positive salary amount (e.g., 50000).",
		};
	}
	// Rule for minimum salary for tax savings
	if (num < 500000) {
		return {
			valid: false,
			message: "Tax savings are applicable for salary of ₹5,00,000 or more.",
		};
	}
	// All checks passed
	return { valid: true, message: "" };
};
